/**
 * Deliberate main-thread jank, for reproducing the transmitted-audio dropouts
 * ("ブツブツ途切れる") on demand.
 *
 * WHY THIS EXISTS
 * ---------------
 * The cleaned audio reaches the transmitted stream through
 *   render thread → MAIN THREAD → ONNX worker → MAIN THREAD → render thread
 * and the emit worklet only buffers EMIT_QUEUE_CAP(3) × 32 ms ≈ 96 ms.
 *
 * So any main-thread stall longer than ~96 ms punches a hole in what the far
 * side hears — regardless of how fast the machine is, because the stall is a
 * scheduling problem, not a throughput one. That is the standing hypothesis for
 * the dropouts reported on high-spec PCs.
 *
 * This module blocks the main thread on purpose so the hypothesis can be tested
 * in a real call: turn it on, speak, and watch `途切れ=空…ms/溢…ms` in the alive
 * log while the other side listens.
 *   - 溢 (overflow trim) grows  → confirms it: messages piled up while frozen
 *   - nothing changes           → the dropouts come from somewhere else
 *
 * The block is a busy-wait, NOT setTimeout: only a synchronous loop actually
 * freezes the thread the way a long task does.
 *
 * Debug tool. Reachable only when config.nkfAec.jankButton is true.
 */
/** Handle for the repeating burst, so it can be stopped. */
let _timer = null;
/** True while a burst is scheduled. */
let _running = false;
/**
 * Freezes the main thread for the given duration. Synchronous on purpose.
 *
 * @param {number} ms - How long to block.
 * @returns {void}
 */
export function blockMainThread(ms) {
    const end = performance.now() + ms;
    // Busy-wait. Anything that yields (await, setTimeout) would let the event
    // loop drain and defeat the whole point.
    while (performance.now() < end) {
        // eslint-disable-next-line no-empty
    }
}
/**
 * Starts blocking the main thread repeatedly until {@link stopJank}.
 *
 * @param {number} blockMs - Length of each freeze.
 * @param {number} gapMs - Idle time between freezes.
 * @returns {void}
 */
export function startJank(blockMs = 150, gapMs = 350) {
    stopJank();
    _running = true;
    console.warn(`[nkf][jank] main-thread jank ON — ${blockMs}ms block / ${gapMs}ms gap`);
    const tick = () => {
        if (!_running) {
            return;
        }
        blockMainThread(blockMs);
        _timer = setTimeout(tick, gapMs);
    };
    tick();
}
/**
 * Stops the repeating block.
 *
 * @returns {void}
 */
export function stopJank() {
    if (_timer) {
        clearTimeout(_timer);
        _timer = null;
    }
    if (_running) {
        console.warn('[nkf][jank] main-thread jank OFF');
    }
    _running = false;
}
/**
 * Whether a burst is currently running.
 *
 * @returns {boolean}
 */
export function isJankRunning() {
    return _running;
}
// Console access as well as the toolbar button — handy when reproducing without
// taking your hands off the call.
//   __nkfJank()        → start with the defaults
//   __nkfJank(500)     → one 500 ms freeze
//   __nkfJankStop()    → stop
if (typeof window !== 'undefined') {
    window.__nkfJank = (arg) => {
        if (typeof arg === 'number') {
            blockMainThread(arg);
            return;
        }
        startJank();
    };
    window.__nkfJankStop = stopJank;
}
